import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { confirmarRelato, marcarUtil } from "../services/api";
import ShareButtons from "./ShareButtons";
import { teclaAtiva } from "../utils/teclaAtiva";

// Pílula de status usando a cor que vem do backend (StatusCor).
const StatusPill = ({ texto, cor }) => (
  <span
    className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white"
    style={{ backgroundColor: cor || "#9ca3af" }}
  >
    {texto}
  </span>
);

// utc=true para DATAS-only (ex.: dataProcesso, gravada à meia-noite UTC): renderiza a data-calendário
// escolhida sem deslocar pelo fuso (senão um BR/UTC-3 veria o dia anterior). Para INSTANTES (criadoEm), local.
function fmtData(iso, utc) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR", utc ? { timeZone: "UTC" } : undefined); } catch { return iso; }
}

// Interações do card: "👍 Útil" (qualquer um, dedup por localStorage) e "Também aconteceu comigo" (logado, não-autor).
function CardInteracoes({ c }) {
  const { user } = useAuth();
  const [conf, setConf] = useState(c.totalConfirmacoes || 0);
  const [uteis, setUteis] = useState(c.uteis || 0);
  const [confFeito, setConfFeito] = useState(false);
  const [erro, setErro] = useState(null);
  const utilKey = `eairh_util_${c.id}`;
  const [utilFeito, setUtilFeito] = useState(() => typeof window !== "undefined" && localStorage.getItem(utilKey) === "1");

  const ehAutor = user && c.ehMeu;
  const podeConfirmar = user && user.tipo === 0 && !ehAutor;

  async function util(e) {
    e.stopPropagation();
    if (utilFeito) return;
    try { const r = await marcarUtil(c.id); setUteis(r.uteis); setUtilFeito(true); localStorage.setItem(utilKey, "1"); } catch { /* ignora */ }
  }
  async function confirmar(e) {
    e.stopPropagation();
    setErro(null);
    try { const r = await confirmarRelato(c.id); setConf(r.total); setConfFeito(true); }
    catch (err) { setErro(err.message); setConfFeito(true); }
  }

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2.5" onClick={(e) => e.stopPropagation()}>
      <button onClick={util} disabled={utilFeito}
        className={`text-xs font-semibold rounded-full px-3 py-1 border transition ${utilFeito ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
        👍 Útil{uteis > 0 ? ` · ${uteis}` : ""}
      </button>
      {podeConfirmar && (
        <button onClick={confirmar} disabled={confFeito}
          className={`text-xs font-semibold rounded-full px-3 py-1 border transition ${confFeito ? "bg-green-50 text-green-700 border-green-200" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
          ➕ Também aconteceu comigo{conf > 0 ? ` · ${conf}` : ""}
        </button>
      )}
      {!podeConfirmar && conf > 0 && (
        <span className="text-xs text-gray-500">👥 {conf} confirmaç{conf === 1 ? "ão" : "ões"}</span>
      )}
      {erro && <span className="text-xs text-red-500">{erro}</span>}
      <ShareButtons compact url={`/relato/${c.id}`} text={`Relato sobre o processo seletivo da ${c.empresa || "empresa"} no eaiRH:`} className="ml-auto" />
    </div>
  );
}

// Tamanho de página responsivo: 8 no mobile, 12 no desktop (>= 768px).
function useResponsivePageSize() {
  const calc = () => (typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches ? 12 : 8);
  const [size, setSize] = useState(calc);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const fn = () => setSize(mq.matches ? 12 : 8);
    if (mq.addEventListener) mq.addEventListener("change", fn); else mq.addListener(fn);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", fn); else mq.removeListener(fn); };
  }, []);
  return size;
}

export default function ComplaintList({ complaints = [], onSelect, pageSize: pageSizeProp, hideEmpresa = false }) {
  const autoSize = useResponsivePageSize();
  const pageSize = pageSizeProp ?? autoSize; // 8 mobile / 12 desktop (ou fixo, se passado)
  const [page, setPage] = useState(1);
  const topRef = useRef(null);
  const total = complaints.length;
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  // Sempre que a lista muda (ex.: aplicou filtro/busca), volta para a 1ª página.
  useEffect(() => { setPage(1); }, [total]);

  // Garante que a página atual é válida e rola para o topo da lista ao trocar.
  const paginaAtual = Math.min(page, totalPaginas);
  const inicio = (paginaAtual - 1) * pageSize;
  const visiveis = complaints.slice(inicio, inicio + pageSize);

  function irPara(p) {
    setPage(p);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (!complaints.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
        Nenhum relato por aqui ainda. Seja o primeiro a relatar!
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={topRef}>
      {total > pageSize && (
        <p className="text-xs text-gray-500">
          Mostrando <b>{inicio + 1}–{Math.min(inicio + pageSize, total)}</b> de <b>{total}</b> relatos
        </p>
      )}
      {visiveis.map((c) => (
        <article
          key={c.id}
          onClick={() => onSelect?.(c)}
          {...teclaAtiva(() => onSelect?.(c), null)}
          aria-label={`Abrir relato: ${c.titulo} — ${c.empresa}`}
          className="cursor-pointer rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm transition hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <header className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {!hideEmpresa && <h4 className="text-xs font-semibold text-gray-500">{c.empresa}</h4>}
              <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug">
                {c.titulo}
              </h3>
              {c.vaga && <p className="text-xs text-gray-500 mt-0.5">Vaga: {c.vaga} · {c.etapaTexto}</p>}
            </div>
            <div className="text-right shrink-0">
              {/* Status (Resolvida/Sem resposta/...) só faz sentido para RECLAMAÇÃO. Elogio/neutro mostram a natureza. */}
              {c.natureza === 1
                ? <span className="text-xs font-semibold rounded-full bg-green-100 text-green-700 px-2.5 py-1">😊 Elogio</span>
                : c.natureza === 2
                ? <span className="text-xs font-semibold rounded-full bg-gray-100 text-gray-600 px-2.5 py-1">😐 Relato</span>
                : <StatusPill texto={c.statusTexto} cor={c.statusCor} />}
              {/* A data do CARD é a da publicação (criadoEm) — é o que "mais recentes" ordena e o que o leitor
                  espera de um feed. A data do PROCESSO vira contexto embaixo, quando for de outro período: antes,
                  um relato publicado hoje sobre um processo de 2020 aparecia carimbado como 2020. */}
              <div className="mt-1.5 text-xs text-gray-500">{fmtData(c.criadoEm)}</div>
              {c.dataProcesso && fmtData(c.dataProcesso, true) !== fmtData(c.criadoEm) && (
                <div className="text-[11px] text-gray-400">processo em {fmtData(c.dataProcesso, true)}</div>
              )}
            </div>
          </header>

          {c.descricao && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">{c.descricao}</p>
          )}

          <div className="mt-2 flex flex-wrap gap-2">
            {c.historico && (
              <span className="text-xs font-semibold rounded-full bg-gray-100 text-gray-600 px-2.5 py-1" title="Processo de mais de 90 dias — registrado para o histórico, sem cobrança ativa à empresa">
                🕰️ Relato histórico
              </span>
            )}
            {c.resposta && (
              <span className="text-xs font-semibold rounded-full bg-blue-100 text-blue-700 px-2.5 py-1">
                💬 Empresa respondeu
              </span>
            )}
            {c.avaliacao && (
              <span className="text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 px-2.5 py-1">
                ⭐ {c.avaliacao.nota}/10
              </span>
            )}
          </div>

          <div className="mt-2 flex justify-end">
            <Link to={`/relato/${c.id}`} onClick={(e) => e.stopPropagation()}
              className="text-xs font-bold text-primary hover:underline">
              Ler relato completo →
            </Link>
          </div>

          <CardInteracoes c={c} />
        </article>
      ))}

      {totalPaginas > 1 && (
        <nav className="flex items-center justify-center gap-1.5 pt-2" aria-label="Paginação">
          <BtnPag onClick={() => irPara(paginaAtual - 1)} disabled={paginaAtual === 1} label="Página anterior">‹</BtnPag>
          {Array.from({ length: totalPaginas }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPaginas || Math.abs(p - paginaAtual) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…" + p);
              acc.push(p);
              return acc;
            }, [])
            .map((p) =>
              typeof p === "string" ? (
                <span key={p} className="px-2 text-gray-400 select-none">…</span>
              ) : (
                <BtnPag key={p} onClick={() => irPara(p)} ativo={p === paginaAtual}>{p}</BtnPag>
              )
            )}
          <BtnPag onClick={() => irPara(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} label="Próxima página">›</BtnPag>
        </nav>
      )}
    </div>
  );
}

function BtnPag({ children, onClick, ativo, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`min-w-[2rem] h-8 px-2 rounded-lg text-sm font-semibold border transition
        ${ativo ? "bg-primary text-white border-transparent"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}
        ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}
